/**
 * TrustedShareReceiptsPanel — collapsible per-checklist delivery
 * receipts view. Lists each Trusted Circle contact with their *latest*
 * delivery state (sent / failed / skipped / not-yet-sent) and offers a
 * one-tap retry that re-invokes `send-trusted-share` filtered to only
 * the failed/skipped contacts.
 */
import { useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Mail,
  MailX,
  RefreshCcw,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useTrustedCircle } from "@/lib/safety/useTrustedCircle";
import { useTrustedShareReceipts } from "@/lib/safety/useTrustedShareReceipts";
import { useTrustedShareNotifier } from "@/lib/safety/useTrustedShareNotifier";
import type { PreMeetChecklist } from "@/lib/safety/usePreMeet";

interface Props {
  checklist: PreMeetChecklist;
}

const STATUS_LABEL: Record<string, string> = {
  sent: "Delivered",
  failed: "Failed",
  skipped: "Skipped",
  pending: "Pending",
  none: "Not sent yet",
};

const TrustedShareReceiptsPanel = ({ checklist }: Props) => {
  const [open, setOpen] = useState(false);
  const { contacts } = useTrustedCircle();
  const { latestByContact, counts, loading, refresh } = useTrustedShareReceipts(
    open ? checklist.id : null,
  );
  const { notify } = useTrustedShareNotifier();
  const [retrying, setRetrying] = useState(false);

  // Build the contact-by-contact view in Trusted Circle order.
  const rows = useMemo(
    () =>
      contacts.map((c) => {
        const r = latestByContact.get(c.id);
        return {
          contact: c,
          status: (r?.status ?? "none") as
            | "sent"
            | "failed"
            | "skipped"
            | "pending"
            | "none",
          error: r?.error_message ?? null,
          at: r?.created_at ?? null,
        };
      }),
    [contacts, latestByContact],
  );

  const retryableIds = useMemo(
    () =>
      rows
        .filter(
          (r) =>
            r.status === "failed" ||
            (r.status === "skipped" && !!r.contact.email),
        )
        .map((r) => r.contact.id),
    [rows],
  );

  const handleRetry = async () => {
    if (retryableIds.length === 0) {
      toast.info("Nothing to retry.");
      return;
    }
    setRetrying(true);
    try {
      const res = await notify(checklist, { contactIds: retryableIds });
      if (res.error) {
        // notify() already toasted
      } else if (res.sent > 0) {
        toast.success(
          `Retried ${res.sent} of ${retryableIds.length} contact${
            retryableIds.length === 1 ? "" : "s"
          }.`,
        );
      }
      await refresh();
    } finally {
      setRetrying(false);
    }
  };

  if (contacts.length === 0) return null;

  return (
    <div className="mt-3 border-t border-border pt-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1.5"
      >
        <Mail className="h-3 w-3" />
        Delivery receipts
        {open && counts.total > 0 && (
          <span className="ml-1 normal-case tracking-normal text-muted-foreground/80">
            · {counts.sent} sent
            {counts.failed > 0 ? ` · ${counts.failed} failed` : ""}
            {counts.skipped > 0 ? ` · ${counts.skipped} skipped` : ""}
          </span>
        )}
      </button>

      {open && (
        <div className="mt-2 space-y-1.5">
          {loading ? (
            <p className="text-xs text-muted-foreground">Loading…</p>
          ) : (
            rows.map((r) => (
              <div
                key={r.contact.id}
                className="flex items-start justify-between gap-2 rounded-md bg-muted/30 px-2 py-1.5 text-xs"
              >
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-foreground truncate">
                    {r.contact.name}
                  </p>
                  <p className="text-muted-foreground truncate">
                    {r.contact.email ?? "no email on file"}
                  </p>
                  {r.error && (
                    <p className="text-destructive text-[10px] mt-0.5 truncate">
                      {r.error}
                    </p>
                  )}
                </div>
                <div className="shrink-0 inline-flex items-center gap-1">
                  {r.status === "sent" && (
                    <CheckCircle2 className="h-3.5 w-3.5 text-success" />
                  )}
                  {r.status === "failed" && (
                    <AlertTriangle className="h-3.5 w-3.5 text-destructive" />
                  )}
                  {r.status === "skipped" && (
                    <MailX className="h-3.5 w-3.5 text-muted-foreground" />
                  )}
                  {r.status === "pending" && (
                    <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                  )}
                  {r.status === "none" && (
                    <Clock className="h-3.5 w-3.5 text-muted-foreground/60" />
                  )}
                  <span
                    className={
                      r.status === "sent"
                        ? "text-success"
                        : r.status === "failed"
                          ? "text-destructive"
                          : "text-muted-foreground"
                    }
                  >
                    {STATUS_LABEL[r.status]}
                  </span>
                </div>
              </div>
            ))
          )}

          {retryableIds.length > 0 && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => void handleRetry()}
              disabled={retrying}
              className="mt-1 h-7 text-xs"
            >
              <RefreshCcw className="h-3 w-3 mr-1.5" />
              Retry {retryableIds.length} failed
              {retryableIds.length === 1 ? "" : " contacts"}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default TrustedShareReceiptsPanel;
